import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { supabase } from "../supabaseClient.js";
// DashboardNavbar removed
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitType from "split-type";
import * as LucideIcons from "lucide-react";

gsap.registerPlugin(useGSAP, ScrollTrigger);

const ICON_BUCKET = "assets";

const DynamicIcon = ({ name, className }) => {
  const Icon = LucideIcons[name] || LucideIcons.Box;
  return <Icon className={className} />;
};



import CacheService from "../services/CacheService.js";

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Get context from Layout
  const { setPageTitle, profile: ctxProfile } = useOutletContext();

  const containerRef = useRef(null);
  const heroTitleRef = useRef(null);
  const heroDescRef = useRef(null);

  const featuresRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Init from Context or Cache
  const [profile, setProfile] = useState(() => ctxProfile || CacheService.get('user_profile') || null);

  const [userTools, setUserTools] = useState([]);
  const [loadingTools, setLoadingTools] = useState(true);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);

  // Set page title
  useEffect(() => {
    setPageTitle("Dashboard");
  }, [setPageTitle]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        // 1. 🔐 Cek user login (Blocking)
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        if (authError || !authUser) {
          navigate("/login");
          return;
        }
        setUser(authUser);

        // 2. 🚀 Fetch Data Paralel
        const [profileRes] = await Promise.all([
          supabase.from('profiles').select('*').eq('id', authUser.id).single()
        ]);

        if (profileRes.data) {
          setProfile(profileRes.data);
          // Update Cache
          CacheService.set('user_profile', profileRes.data, 60);
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  // Fetch user tools
  useEffect(() => {
    const fetchUserTools = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setLoadingTools(true);
      const { data: uFeats } = await supabase
        .from("user_features")
        .select("feature_code")
        .eq("user_id", user.id);

      if (uFeats && uFeats.length > 0) {
        const codes = uFeats.map((r) => r.feature_code);
        const { data: apps } = await supabase
          .from("app_features")
          .select("*")
          .in("slug", codes)
          .eq("is_active", true)
          .order("sort_order");

        if (apps) setUserTools(apps);
      }
      setLoadingTools(false);
    };
    fetchUserTools();
  }, []);

  // Fetch packages when user has no tools
  useEffect(() => {
    const fetchPackages = async () => {
      // Only fetch if tools loading is done and user has no tools
      if (loadingTools || userTools.length > 0) return;

      setPackagesLoading(true);
      try {
        const { data } = await supabase
          .from("credit_packages")
          .select("id, name, price, base_credits, populer, original_price, is_custom_button, button_text, button_url")
          .eq("is_active", true)
          .eq("beranda", true)  // Filter packages marked for homepage
          .order("price", { ascending: true });

        if (data) setPackages(data);
      } catch (error) {
        console.error("Error fetching packages:", error);
      } finally {
        setPackagesLoading(false);
      }
    };

    fetchPackages();
  }, [loadingTools, userTools]);

  useGSAP(() => {
    if (loading) return;

    const tl = gsap.timeline();

    // 1. Hero Animation (Immediate)
    const titleSplit = new SplitType(heroTitleRef.current, { types: 'chars,words' });

    tl.from(titleSplit.chars, {
      y: 80,
      opacity: 0,
      rotateX: -45,
      stagger: 0.03,
      duration: 1.2,
      ease: "expo.out",
    })
      .from(heroDescRef.current, {
        y: 40,
        opacity: 0,
        duration: 1,
        ease: "power3.out"
      }, "-=0.8");

    // 2. Features Grid Animation (ScrollTrigger)
    const cards = gsap.utils.toArray(".feature-card");
    if (cards.length > 0) {
      gsap.from(cards, {
        scrollTrigger: {
          trigger: featuresRef.current,
          scroller: containerRef.current, // CRITICAL: Define the scroll container!
          start: "top 90%", // Start earlier
          toggleActions: "play none none reverse",
        },
        y: 50,
        opacity: 0,
        duration: 0.6,
        stagger: 0.05,
        ease: "power2.out",
        clearProps: "all" // Ensure cleanliness after animation
      });
    }

  }, { scope: containerRef, dependencies: [loading] });

  // Smooth Soft Tilt
  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -5;
    const rotateY = ((x - centerX) / centerX) * 5;

    gsap.to(card, {
      rotateX: rotateX,
      rotateY: rotateY,
      scale: 1.02,
      duration: 0.4,
      ease: "power2.out"
    });
  };

  const handleMouseLeave = (e) => {
    gsap.to(e.currentTarget, {
      rotateX: 0,
      rotateY: 0,
      scale: 1,
      duration: 0.5,
      ease: "elastic.out(1, 0.9)"
    });
  };

  const getIconUrl = (raw) => {
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    try {
      const { data } = supabase.storage.from(ICON_BUCKET).getPublicUrl(raw);
      return data?.publicUrl || null;
    } catch {
      return null;
    }
  };

  const cleanDescription = (desc) => {
    if (!desc) return "AI Tool";

    // Remove configuration blocks like |||CONFIGIRE|||... or similar patterns
    let cleaned = desc.replace(/\|\|\|CONFIG[A-Z]*\|\|\|.*$/gi, '').trim();

    // Remove JSON-like patterns {"name":"...", "url":"..."} etc
    cleaned = cleaned.replace(/\{[^}]*\}/g, '').trim();

    // Remove |||... patterns
    cleaned = cleaned.replace(/\|\|\|.*/g, '').trim();

    // If nothing left after cleaning, return default
    if (!cleaned || cleaned.length < 3) return "AI Tool";

    return cleaned;
  };

  const handleToolClick = (slug) => {
    if (!slug) return;
    navigate(`/dashboard/feature/${slug}`);
  };

  if (loading) return (
    <div className="h-full w-full bg-slate-50 p-6 flex flex-col space-y-8 animate-pulse overflow-hidden">
      {/* Content Loading (No Header) */}
      <div className="w-full h-64 bg-slate-200 rounded-3xl"></div>
      {/* Grid Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-40 bg-slate-200 rounded-2xl"></div>
        ))}
      </div>
    </div>
  );



  return (
    <main className="h-full w-full overflow-y-auto relative scrollbar-hide" id="dashboard-container" ref={containerRef}>
      {/* Modern Grid Background */}
      <div className="absolute inset-0 z-0 opacity-[0.4] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#CBD5E1 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }}
      />

      <div className="max-w-7xl mx-auto px-6 pt-6 pb-12 relative z-10 w-full">

        {/* HERO SECTION */}
        <div className="flex flex-col items-center text-center mb-20 mt-0 w-full">

          <h1 ref={heroTitleRef} className="text-[clamp(1.2rem,5.5vw,4.5rem)] font-bold font-['Special_Gothic_Expanded_One'] text-slate-900 tracking-tight leading-[1.1] mb-6 whitespace-nowrap w-full overflow-visible px-1">
            {t('dashboard.welcome')} <span className="text-sky-600">{t('dashboard.toNeo')}</span>
          </h1>
          <div ref={heroDescRef} className="max-w-3xl mx-auto flex flex-col items-center px-4 w-full">
            <p className="text-[clamp(1rem,4vw,1.5rem)] font-medium text-slate-600 mb-4 px-2 leading-tight">
              {t('dashboard.subtitle')}
            </p>
            <p className="text-[clamp(0.85rem,3vw,1rem)] text-slate-500 leading-relaxed max-w-xl mx-auto w-full">
              {t('dashboard.description')}
            </p>
          </div>
        </div>

        {/* FEATURES GRID */}
        <div ref={featuresRef} className="pb-24 w-full">
          {/* Header - only show if has tools or loading */}
          {(loadingTools || userTools.length > 0) && (
            <div className="flex items-center gap-4 mb-10 w-full">
              <div className="h-px bg-slate-200 flex-1"></div>
              <h2 className="text-xl font-bold text-slate-400 uppercase tracking-widest text-center">{t('dashboard.availableTools')}</h2>
              <div className="h-px bg-slate-200 flex-1"></div>
            </div>
          )}

          {loadingTools ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full" />
            </div>
          ) : userTools.length === 0 ? (
            <div className="space-y-8">
              {/* Header Message */}
              <div className="text-center">
                <h3 className="text-xl font-bold text-slate-800 mb-2">
                  {t('dashboard.activateTools')}
                </h3>
                <p className="text-slate-500 text-sm max-w-md mx-auto">
                  {t('dashboard.activateSubtitle')}
                </p>
              </div>

              {/* Packages Grid */}
              {packagesLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
                </div>
              ) : packages.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
                  {packages.map(pkg => (
                    <div
                      key={pkg.id}
                      onClick={() => !pkg.is_custom_button && navigate('/dashboard/topup')}
                      className={`relative rounded-2xl border bg-white p-6 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 ${pkg.populer
                        ? 'border-amber-400 shadow-md shadow-amber-200 bg-gradient-to-b from-amber-50 to-white'
                        : 'border-slate-200 shadow hover:shadow-md'
                        }`}
                    >
                      {pkg.populer && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-amber-400 text-[11px] font-bold text-amber-900 shadow-md">
                          {t('dashboard.popular')}
                        </div>
                      )}
                      <div className="text-center mb-4">
                        <h3 className="text-lg font-bold text-slate-900 mb-2">{pkg.name}</h3>
                        {pkg.original_price && Number(pkg.original_price) > Number(pkg.price) && (
                          <div className="text-sm text-slate-400 line-through">
                            Rp {Number(pkg.original_price).toLocaleString('id-ID')}
                          </div>
                        )}
                        <div className="text-3xl font-extrabold text-blue-600 mb-1">
                          {Number(pkg.price) === 0 ? t('dashboard.free') : `Rp ${Number(pkg.price).toLocaleString('id-ID')}`}
                        </div>
                        {!pkg.is_custom_button && (
                          <p className="text-sm text-slate-600">
                            {t('dashboard.getCredits', { count: pkg.base_credits?.toLocaleString('id-ID') })}
                          </p>
                        )}
                      </div>
                      {pkg.is_custom_button ? (
                        <a
                          href={pkg.button_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="block w-full py-2.5 rounded-lg font-semibold text-center bg-slate-800 text-white hover:bg-slate-900 transition-colors"
                        >
                          {pkg.button_text || t('dashboard.contactUs')}
                        </a>
                      ) : (
                        <button className={`w-full py-2.5 rounded-lg font-semibold transition-colors ${Number(pkg.price) === 0
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}>
                          {Number(pkg.price) === 0 ? t('dashboard.claimFree') : t('dashboard.viewDetail')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-400 text-sm">
                    {t('dashboard.noPackages')}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 perspective-1200 w-full">
              {userTools.map((tool) => {
                const rawIcon = tool.icon_url || tool.lucide_icon || tool.icon;
                const isImage = rawIcon && (rawIcon.includes('/') || rawIcon.includes('.'));
                const iconUrl = isImage ? getIconUrl(rawIcon) : null;
                const isLucide = rawIcon && !isImage;
                const toolName = tool.name || tool.slug || "Tool";
                const toolDesc = cleanDescription(tool.description);

                return (
                  <div
                    key={tool.id || tool.slug}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleToolClick(tool.slug)}
                    className="feature-card group relative bg-white/70 backdrop-blur-xl rounded-2xl p-4 border border-white/60 shadow-[0_4px_20px_-12px_rgba(0,0,0,0.1)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.15)] hover:border-blue-200/50 transition-all duration-300 transform-gpu preserve-3d cursor-pointer"
                  >
                    <div className="aspect-square bg-white/50 rounded-xl overflow-hidden relative mb-4 flex items-center justify-center p-5 group-hover:bg-blue-50/50 transition-colors duration-500">
                      {/* Subtle Inner Glow */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-blue-400/0 via-white/0 to-white/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                      {isImage && iconUrl ? (
                        <img
                          src={iconUrl}
                          alt={toolName}
                          className="w-full h-full object-contain drop-shadow-sm transform group-hover:scale-110 group-hover:rotate-2 transition-transform duration-500 cubic-bezier(0.34, 1.56, 0.64, 1)"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : isLucide ? (
                        <div className="transform group-hover:scale-110 group-hover:rotate-2 transition-transform duration-500 cubic-bezier(0.34, 1.56, 0.64, 1)">
                          <DynamicIcon name={rawIcon} className="w-20 h-20 text-blue-600" />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold bg-gradient-to-br from-blue-500 to-purple-500 text-white transform group-hover:scale-110 group-hover:rotate-2 transition-transform duration-500 cubic-bezier(0.34, 1.56, 0.64, 1)">
                          {toolName.charAt(0)}
                        </div>
                      )}
                    </div>

                    <div className="text-center relative z-10">
                      <h3 className="font-bold text-slate-800 text-sm mb-1 group-hover:text-blue-600 transition-colors">{toolName}</h3>
                      <p className="text-[10px] text-slate-400 font-medium leading-tight line-clamp-2">{toolDesc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </main>
  );
};

export default DashboardPage;
